/**
 * HierarchicalAssetReview — Step 2's view when the project's
 * `analysis_mode === 'hierarchical'` (a parent TARA aggregating one
 * or more child TARAs).
 *
 * Two tabs via the shared TableTabNav:
 *   - Current TARA  — newly identified system-level assets, full CRUD
 *                     via AssetTable.
 *   - Child TARAs   — inherited assets with per-row relevance toggle
 *                     + justification.
 *
 * Pure controlled component: `modifications` + the two setters come
 * from the Step 2 hook (so the global ActionPanel's "Approve &
 * Proceed" button can read the same state when it fires
 * `approveHierarchicalData`). No in-tab approve button here.
 */
import React, { useMemo, useState } from 'react';
import TableTabNav from '../../components/TableTabNav/TableTabNav';
import AssetTable from './AssetTable';
import ChildAssetsTable, { type ChildAssetView } from './ChildAssetsTable';
import type { AssetRecord } from './assetFieldSchema';
import type { HierarchicalModifications } from '../stepBodyProps';

const TAB_CURRENT = 'current';
const TAB_CHILD = 'child';

export interface HierarchicalAssetReviewProps {
  newAssets: AssetRecord[];
  childAssets: ChildAssetView[];
  editable: boolean;
  /** Current-TARA CRUD handlers — only used in the Current TARA tab. */
  onAdd: () => void;
  onEdit: (asset: AssetRecord) => void;
  onDelete: (asset: AssetRecord) => void;
  /** Optional export + import handlers — wired through to the
   *  Current TARA's AssetTable toolbar. Child TARAs tab is read-only
   *  on the data side so no export/import slot lives there. */
  onExport?: () => void;
  onImport?: (file: File) => void;
  /** Controlled child-asset modifications + setters from the hook. */
  modifications: HierarchicalModifications;
  onToggleRelevance: (assetId: string, isRelevant: boolean) => void;
  onJustificationChange: (assetId: string, justification: string) => void;
  /** Disable child-asset inputs (e.g. while an approve is in flight). */
  childInputsDisabled?: boolean;
  /** Persist child-asset modifications on Done (Story 15.F). Wired
   *  to `hierarchical.onApprove` (= `approveHierarchicalData`) which
   *  only saves; workflow advance stays on the global Approve button. */
  onSaveChildModifications?: () => Promise<void> | void;
}

export default function HierarchicalAssetReview({
  newAssets,
  childAssets,
  editable,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
  modifications,
  onToggleRelevance,
  onJustificationChange,
  childInputsDisabled,
  onSaveChildModifications,
}: HierarchicalAssetReviewProps) {
  const [activeTab, setActiveTab] = useState<string>(TAB_CURRENT);

  // Merge modifications into the inherited rows so checkboxes /
  // justification inputs reflect the latest user edits.
  const childView: ChildAssetView[] = useMemo(
    () =>
      childAssets.map((c) => ({
        ...c,
        is_relevant: modifications[c.asset_id]?.is_relevant ?? c.is_relevant ?? true,
        relevance_justification:
          modifications[c.asset_id]?.relevance_justification ?? c.relevance_justification ?? '',
      })),
    [childAssets, modifications],
  );

  const handleToggle = (assetId: string) => {
    const current = childView.find((c) => c.asset_id === assetId);
    onToggleRelevance(assetId, !(current?.is_relevant !== false));
  };

  const tabs = [
    { key: TAB_CURRENT, label: `Current TARA (${newAssets.length})` },
    { key: TAB_CHILD, label: `Child TARAs (${childAssets.length})` },
  ];

  return (
    <>
      <TableTabNav
        tabs={tabs}
        activeKey={activeTab}
        onChange={setActiveTab}
        ariaLabel="Hierarchical asset review tabs"
        bleed={false}
        sticky={false}
      />

      {activeTab === TAB_CURRENT ? (
        <AssetTable
          assets={newAssets}
          editable={editable}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onExport={onExport}
          onImport={onImport}
          emptyMessage="No new system-level assets identified yet."
        />
      ) : (
        <ChildAssetsTable
          assets={childView}
          onToggleRelevance={handleToggle}
          onJustificationChange={onJustificationChange}
          disabled={childInputsDisabled}
          onSave={onSaveChildModifications}
        />
      )}
    </>
  );
}
